import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "../../utils/toast";
import { bookingsApi, postsApi } from "../../services";
import { getSocket } from "../../services/socket";
import { Context } from "../../Context/Context";
import DailyNote from "../../components/booking/DailyNote";
import AccEditModal from "../../components/booking/AccEditModal";
import { rankPlainLabelOf } from "../../utils/rank";
import "./bookings.css";

function formatDateTime(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("vi-VN", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// Hiển thị mốc kết thúc với giờ:phút được bôi nổi bật, kèm ngày phía sau.
function EndDateTime({ date }) {
  if (!date) return <span className="cell-text endat-text">—</span>;
  const d = new Date(date);
  const time = d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
  const day = d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" });
  return (
    <span className="cell-text endat-text">
      <span className="endat-time">{time}</span> {day}
    </span>
  );
}

// Tag nguồn gốc phiếu: "Auto" (đồng bộ từ hệ thống thứ 3) + "Đã sửa tay" (admin
// chỉnh tay sau khi tạo — job đồng bộ external sẽ không tự ghi đè phiếu này nữa).
function SourceTag({ source, manuallyEditedAt }) {
  if (source !== "external" && !manuallyEditedAt) return null;
  return (
    <span className="source-tag-group">
      {source === "external" && (
        <span className="source-tag source-tag-auto" title="Tự động đồng bộ từ hệ thống thuê acc bên thứ 3">
          <i className="fas fa-sync-alt"></i> Auto
        </span>
      )}
      {manuallyEditedAt && (
        <span
          className="source-tag source-tag-edited"
          title={`Admin đã chỉnh tay lúc ${formatDateTime(manuallyEditedAt)} — không còn tự động đồng bộ`}
        >
          <i className="fas fa-user-edit"></i> Đã sửa tay
        </span>
      )}
    </span>
  );
}

function formatCurrency(num) {
  if (!num) return "0₫";
  return Number(num).toLocaleString("vi-VN") + "₫";
}

// Trang "Quy định thuê acc" theo từng game — hiện chỉ Liên Quân đã có trang này.
const GAME_RULE_URLS = {
  lienquan: "https://hoanglecf.com/cong-dong/quy-dinh-thue-acc-lien-quan-mobile",
};

// Dựng đoạn text gửi khách khi cho thuê, vd:
//   LQ650|daitoan|daitoan123
//   uy tín 100
//   rank tinh anh 1
//   https://hoanglecf.com/cong-dong/quy-dinh-thue-acc-lien-quan-mobile
function buildAccCopyText(post, creds) {
  const lines = [`${post.title}|${creds.accUsername || ""}|${creds.accPassword || ""}`];
  if (post.reputation !== null && post.reputation !== undefined) lines.push(`uy tín ${post.reputation}`);
  if (post.rank?.tier) lines.push(`rank ${rankPlainLabelOf(post.rank)}`);
  if (GAME_RULE_URLS[post.thumb]) lines.push(GAME_RULE_URLS[post.thumb]);
  return lines.join("\n");
}

// Ô "Acc" dùng chung cho bảng acc lẫn bảng phiếu: bấm ID để copy thông tin gửi
// khách; icon riêng để vẫn mở được trang chi tiết (xem ảnh) + icon sửa acc tại chỗ.
// Chỉ Liên Quân cần 2 việc này (gửi kèm rank/uy tín/quy định, thông tin acc hay
// đổi) → game khác chỉ hiện ID acc dạng link mở trang chi tiết cho gọn.
function AccCell({ postId, title, viewTo, onCopy, onEditAcc, showAccTools }) {
  if (!showAccTools) {
    return (
      <div className="acc-id-row">
        <Link to={viewTo} className="acc-link acc-link-compact" title="Xem chi tiết / ảnh">
          {title}
        </Link>
      </div>
    );
  }
  return (
    <div className="acc-id-row">
      <button
        type="button"
        className="acc-link acc-link-compact acc-copy-btn"
        title="Bấm để copy id|user|pass gửi khách"
        onClick={onCopy}
      >
        {title}
      </button>
      <Link to={viewTo} className="acc-view-link" title="Xem chi tiết / ảnh">
        <i className="fas fa-external-link-alt"></i>
      </Link>
      <button
        type="button"
        className="acc-edit-btn acc-edit-profile-btn"
        title="Sửa thông tin acc"
        onClick={onEditAcc}
      >
        <i className="fas fa-address-card"></i>
      </button>
    </div>
  );
}

// Số giờ thuê = (endAt - startAt) / giờ, làm tròn 1 chữ số thập phân
function calcHours(startAt, endAt) {
  if (!startAt || !endAt) return null;
  const s = new Date(startAt).getTime();
  const e = new Date(endAt).getTime();
  if (isNaN(s) || isNaN(e) || e <= s) return null;
  return Math.round(((e - s) / 3600000) * 10) / 10;
}

function toLocalInput(date) {
  if (!date) return "";
  const d = new Date(date);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

// Ngày theo giờ VN (+07) dạng YYYY-MM-DD. offsetH = số giờ cộng thêm trước
// khi lấy ngày-lịch: 0 = ngày lịch hiện tại; 2 = "ca kinh doanh đang chạy"
// (sau 22h sẽ nhảy sang ngày kế, do mốc đổi ngày là 22h).
function vnDateStr(offsetH = 0) {
  const dt = new Date(Date.now() + (7 + offsetH) * 60 * 60 * 1000);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Dịch chuỗi YYYY-MM-DD thêm/bớt số ngày
function shiftDateStr(str, delta) {
  const [y, m, d] = str.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + delta));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

// Hiển thị YYYY-MM-DD thành DD/MM/YYYY
function formatDateStr(str) {
  const [y, m, d] = str.split("-");
  return `${d}/${m}/${y}`;
}

// Ngày KINH DOANH (giờ VN, mốc đổi ngày 22h) của 1 thời điểm → YYYY-MM-DD.
// Khớp với cách tính doanh thu ở BE: ngày D = [22:00 (D-1) … 22:00 D).
// Cộng (7+2)h: dời 22:00 VN về 00:00 ngày kế rồi lấy ngày-lịch UTC.
function businessDayOf(date) {
  if (!date) return "";
  const dt = new Date(new Date(date).getTime() + (7 + 2) * 60 * 60 * 1000);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function defaultRowInput() {
  return {
    customer: "",
    hours: "",
    startAt: toLocalInput(new Date()),
    endAt: "",
    priceAmount: "",
  };
}

// Suy ra giá trị số (VND) từ chuỗi giá của phiếu (booking không lưu priceValue).
// VD: "3h/30.000" → 30000, "75k/h" → 75000. Lấy con số lớn nhất trong chuỗi.
function parsePriceValue(str) {
  if (!str) return 0;
  const matches = String(str).toLowerCase().match(/\d[\d.,]*\s*k?/g);
  if (!matches) return 0;
  let max = 0;
  for (const m of matches) {
    const hasK = m.includes("k");
    let n = parseInt(m.replace(/[^\d]/g, ""), 10) || 0;
    if (hasK && n < 1000) n *= 1000;
    if (n > max) max = n;
  }
  return max;
}

// Mức giá/giờ của 1 phiếu thuê. Ưu tiên priceValue (denormalize từ post lúc
// cho thuê) để màu/phân cụm khớp tab "Tất cả acc"; phiếu cũ chưa có thì suy ra
// từ chuỗi giá (kém chính xác với gói /3h, nhưng chỉ là fallback).
function bookingPriceValue(b) {
  return b.priceValue || parsePriceValue(b.price);
}

// Màu theo mức giá: 75k→đỏ, 60k→vàng, 45k→xanh lá, 30k→xanh dương
function priceTierClass(pv) {
  return pv >= 25000 ? "tier-red"
    : pv >= 20000 ? "tier-yellow"
    : pv >= 15000 ? "tier-green"
    : pv >= 10000 ? "tier-blue"
    : "tier-gray";
}

// Màu phụ cho các gói trùng màu chuẩn (vd 3h/30.000 & 2h/20.000 đều 10k/h).
const TIER_ALTS = ["tier-purple", "tier-teal", "tier-orange", "tier-pink"];

// Gán màu vách ngăn cho từng GÓI (chuỗi giá) để các gói luôn khác màu nhau:
// gói đầu của mỗi màu chuẩn giữ màu đó; gói sau trùng màu lấy màu phụ riêng.
function buildPriceTierMap(items, getPv, getPrice) {
  const groups = [];
  const seen = new Set();
  for (const it of items) {
    const price = getPrice(it) || "";
    if (seen.has(price)) continue;
    seen.add(price);
    groups.push({ price, pv: getPv(it) || 0 });
  }
  groups.sort((a, b) =>
    (b.pv - a.pv) ||
    parsePriceValue(b.price) - parsePriceValue(a.price) ||
    a.price.localeCompare(b.price));
  const usedBase = new Set();
  const map = {};
  let alt = 0;
  for (const g of groups) {
    const base = priceTierClass(g.pv);
    if (!usedBase.has(base)) {
      usedBase.add(base);
      map[g.price] = base;
    } else {
      map[g.price] = TIER_ALTS[alt % TIER_ALTS.length];
      alt++;
    }
  }
  return map;
}

// Khách yêu cầu ẩn cột giá tiền khỏi các bảng — giữ nguyên code, chỉ tắt hiển thị
// qua cờ này để dễ bật lại sau này.
const SHOW_PRICE_COLUMN = true;

// Tab loại game (khớp field `thumb` của post — xem Pages/write/Write.jsx).
// Mặc định CrossFire: đây là nhóm acc chính, khách mở sổ là muốn xem ngay.
const GAME_TABS = [
  { key: "crossFire", label: "CrossFire", icon: "fas fa-crosshairs" },
  { key: "lienquan", label: "Liên Quân", icon: "fas fa-khanda" },
  { key: "freefire", label: "Free Fire", icon: "fas fa-fire" },
];
const GAME_KEYS = GAME_TABS.map((g) => g.key);

// Acc/phiếu có thumb lạ (dữ liệu cũ khi `thumb` còn dùng lưu tên ảnh) hoặc post
// đã bị xoá → xếp vào CrossFire, để không có acc nào bị ẩn khỏi mọi tab.
function gameOf(thumb) {
  return GAME_KEYS.includes(thumb) ? thumb : "crossFire";
}

function buildThumbMap(posts) {
  const map = {};
  posts.forEach((p) => { map[String(p._id)] = p.thumb; });
  return map;
}

const FILTERS = [
  { key: "all", label: "Tất cả acc" },
  { key: "free", label: "Acc trống" },
  { key: "active", label: "Đang thuê" },
  { key: "overdue", label: "Quá hạn" },
  { key: "completed", label: "Đã trả" },
  { key: "cancelled", label: "Đã hủy" },
  { key: "deleted", label: "Đã xóa" },
];

export default function Bookings() {
  const [posts, setPosts] = useState([]);
  const [activeBkgMap, setActiveBkgMap] = useState({});
  const [rowInputs, setRowInputs] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [search, setSearch] = useState("");

  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [game, setGame] = useState("crossFire"); // tab loại game đang xem
  // postId -> thumb, để lọc BẢNG PHIẾU theo game (booking không lưu loại game).
  const [postThumbMap, setPostThumbMap] = useState({});
  const [summary, setSummary] = useState({ active: 0, overdue: 0, deleted: 0 });
  const [revenueDate, setRevenueDate] = useState(() => vnDateStr(0)); // mặc định: ngày lịch VN
  const todayStr = vnDateStr(0);   // ngày lịch hôm nay (giờ VN) — tính lại mỗi render để bắt kịp mốc đổi ngày 22h
  const maxDateStr = vnDateStr(2); // ca đang chạy — giới hạn xem tới đây
  const isRevenueToday = revenueDate === todayStr;
  const canGoNext = revenueDate < maxDateStr;
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [extendHours, setExtendHours] = useState({});
  const [editRow, setEditRow] = useState({}); // postId -> {customer,hours,startAt,endAt,priceAmount}
  const [confirm, setConfirm] = useState(null); // { title, message, confirmLabel, variant, onConfirm }
  const [accEditId, setAccEditId] = useState(null); // postId đang mở modal sửa thông tin acc, null = đóng
  const { user } = useContext(Context);
  const autoRefTimer = useRef(null);
  const dateInputRef = useRef(null);

  // Doanh thu: ẩn mặc định, chỉ gọi API + hiển thị khi nhập đúng password.
  // Password chỉ được kiểm tra ở backend, không lưu trong bundle FE.
  const [revenueUnlocked, setRevenueUnlocked] = useState(false);
  const [revenueValue, setRevenueValue] = useState(null);
  const [revenueInput, setRevenueInput] = useState("");
  const [revenueChecking, setRevenueChecking] = useState(false);
  const [revenueErr, setRevenueErr] = useState("");
  const [showUnlockForm, setShowUnlockForm] = useState(false); // lật thẻ sang mặt nhập mật khẩu
  const revenuePwdRef = useRef(""); // lưu tạm trong RAM để tự tải lại khi đổi ngày / auto-refresh

  const fetchRevenue = useCallback(async (pwd, date) => {
    const data = await bookingsApi.getRevenue(date, pwd);
    setRevenueValue(data?.todayRevenue ?? 0);
  }, []);

  const handleUnlockRevenue = async () => {
    const pwd = revenueInput.trim();
    if (!pwd) return;
    setRevenueChecking(true);
    setRevenueErr("");
    try {
      await fetchRevenue(pwd, revenueDate);
      revenuePwdRef.current = pwd;
      setRevenueUnlocked(true);
      setRevenueInput("");
      setShowUnlockForm(false);
    } catch {
      setRevenueErr("Sai mật khẩu");
    } finally {
      setRevenueChecking(false);
    }
  };

  const handleLockRevenue = () => {
    revenuePwdRef.current = "";
    setRevenueUnlocked(false);
    setRevenueValue(null);
    setRevenueInput("");
    setRevenueErr("");
    setShowUnlockForm(false);
  };

  // Đã mở khoá thì tự tải lại doanh thu khi đổi ngày (dùng password đã nhớ trong RAM)
  useEffect(() => {
    if (!revenueUnlocked || !revenuePwdRef.current) return;
    fetchRevenue(revenuePwdRef.current, revenueDate).catch(() => {
      // Không huỷ unlock khi refetch lỗi tạm thời — chỉ giữ số liệu cũ
    });
  }, [revenueDate, revenueUnlocked, fetchRevenue]);

  // Mở lịch native từ vùng hiển thị ngày tuỳ biến
  const openDatePicker = () => {
    const el = dateInputRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.focus();
  };

  const gameLabel = GAME_TABS.find((g) => g.key === game)?.label || "";
  const isDeletedTab = filter === "deleted";
  const isFreeTab = filter === "free";       // chỉ acc đang trống (chưa ai thuê)
  const isActiveTab = filter === "active";   // chỉ acc đang thuê
  const isAllTab = filter === "all";         // tab "Tất cả acc" — ẩn cột Bắt đầu
  const isCompletedTab = filter === "completed"; // "Đã trả" — nhóm theo NGÀY, mới nhất trước
  // 3 tab dùng chung bảng acc (giao diện "Tất cả acc") + realtime tại chỗ
  const isPostsTab = isAllTab || isFreeTab || isActiveTab;
  // Copy thông tin gửi khách + sửa acc tại chỗ chỉ dùng cho acc Liên Quân
  const isLienQuanTab = game === "lienquan";

  // Thống kê (gồm doanh thu theo ngày kinh doanh đang chọn) — tách riêng để
  // đổi ngày doanh thu không phải tải lại cả bảng.
  const loadSummary = useCallback(async () => {
    try {
      const sum = await bookingsApi.getSummary(revenueDate);
      setSummary(sum || { active: 0, overdue: 0, deleted: 0 });
    } catch { /* giữ số liệu cũ nếu lỗi */ }
  }, [revenueDate]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setSelected([]); // reload nền (socket/auto) không xoá lựa chọn đang thao tác
    try {
      if (filter === "all" || filter === "free" || filter === "active") {
        const [postsData, bkgsData] = await Promise.all([
          postsApi.getAllPosts("?lite=1"), // bỏ listImages cho nhẹ
          bookingsApi.getAll({ status: "active" }),
        ]);
        const filtered = (postsData || []).filter((p) => p.thumb !== "guide");
        // Sắp theo giá/giờ giảm dần; cùng mức giá thì gom theo gói, gói có tổng
        // tiền lớn hơn lên trên (vd "3h/30.000" trên "2h/20.000").
        filtered.sort((a, b) =>
          (b.priceValue || 0) - (a.priceValue || 0) ||
          parsePriceValue(b.price) - parsePriceValue(a.price) ||
          (a.price || "").localeCompare(b.price || ""));
        setPosts(filtered);
        setPostThumbMap(buildThumbMap(filtered));
        const bkgMap = {};
        (bkgsData || []).forEach((b) => { bkgMap[String(b.postId)] = b; });
        setActiveBkgMap(bkgMap);
        setRowInputs((prev) => {
          const next = { ...prev };
          const nowStr = toLocalInput(new Date());
          filtered.forEach((p) => {
            if (p.rentalStatus !== "rented") {
              const ex = next[p._id];
              if (!ex) {
                next[p._id] = defaultRowInput();
              } else if (!ex.customer && !ex.hours && !ex.endAt) {
                // Chưa nhập gì → cập nhật startAt theo giờ hiện tại
                next[p._id] = { ...ex, startAt: nowStr };
              }
            }
          });
          return next;
        });
      } else {
        const opts = {};
        if (filter === "deleted") opts.deleted = true;
        else if (filter === "overdue") opts.overdue = true;
        else opts.status = filter;
        const data = await bookingsApi.getAll(opts);
        setBookings(Array.isArray(data) ? data : []);
      }
    } catch {
      // Reload ngầm (socket/auto/sau thao tác) thất bại thì im lặng — giữ dữ liệu
      // cũ. Chỉ báo lỗi khi người dùng chủ động tải (đổi tab / mở trang).
      if (!silent) toast.error("Không tải được dữ liệu");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filter]);

  // Áp payload realtime trực tiếp vào state, KHÔNG gọi API (0 truy vấn DB).
  // Dùng cho tab "Tất cả acc": phần lớn event từ job là chuyển acc về "available".
  const applyRentalPayload = useCallback((payload) => {
    const pid = String(payload.postId);
    setPosts((prev) =>
      prev.map((p) =>
        String(p._id) === pid
          ? { ...p, rentalStatus: payload.rentalStatus, rentedUntil: payload.rentedUntil }
          : p
      )
    );
    setActiveBkgMap((prev) => {
      if (payload.rentalStatus === "available") {
        if (!prev[pid]) return prev;
        const next = { ...prev }; delete next[pid]; return next;
      }
      return prev; // "rented": cần dữ liệu phiếu đầy đủ → để reload gộp bổ sung
    });
  }, []);

  // Reload gộp: nhiều event dồn trong 1 khoảng ngắn → chỉ 1 lần tải (chống bùng nổ).
  const reloadTimer = useRef(null);
  const scheduleReload = useCallback(() => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => { load(true); }, 800);
  }, [load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]); // mount + khi đổi ngày doanh thu

  // Các tab phiếu không tải danh sách acc, nhưng vẫn cần biết acc thuộc game nào
  // để lọc theo tab game → nạp bản đồ postId→thumb một lần khi còn trống.
  // Tab mặc định là "Tất cả acc" (đã có posts) nên thường không phát sinh request.
  useEffect(() => {
    if (isPostsTab || Object.keys(postThumbMap).length > 0) return;
    let alive = true;
    postsApi
      .getAllPosts("?lite=1")
      .then((data) => { if (alive) setPostThumbMap(buildThumbMap(data || [])); })
      .catch(() => { /* thiếu bản đồ → coi như acc CrossFire, không chặn hiển thị */ });
    return () => { alive = false; };
  }, [isPostsTab, postThumbMap]);

  // Auto-refresh mỗi 60 giây ở mọi tab (đối soát + làm tươi doanh thu nếu đã mở khoá).
  useEffect(() => {
    autoRefTimer.current = setInterval(() => {
      load(true);
      loadSummary();
      if (revenuePwdRef.current) {
        fetchRevenue(revenuePwdRef.current, revenueDate).catch(() => {});
      }
    }, 60000);
    return () => clearInterval(autoRefTimer.current);
  }, [load, loadSummary, fetchRevenue, revenueDate]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;
    const handler = (payload) => {
      if (!payload || !payload.postId) return;
      if (isPostsTab) {
        // Cập nhật tại chỗ — không tải lại. Chỉ "rented" (acc do nơi khác cho thuê)
        // mới cần phiếu đầy đủ → reload gộp để bổ sung chi tiết.
        applyRentalPayload(payload);
        if (payload.rentalStatus === "rented") scheduleReload();
      } else {
        // Các tab phiếu: gộp burst event thành 1 lần tải.
        scheduleReload();
      }
      // KHÔNG gọi loadSummary ở đây: auto-expire (active→completed) không đổi doanh thu;
      // admin tự thao tác đã gọi loadSummary riêng.
    };
    socket.on("post:rental", handler);
    return () => {
      socket.off("post:rental", handler);
      if (reloadTimer.current) clearTimeout(reloadTimer.current);
    };
  }, [isPostsTab, applyRentalPayload, scheduleReload]);

  // 3-chiều: hours ↔ startAt ↔ endAt, đồng thời tính priceAmount = hours × priceValue/h
  const setRowField = (postId, field, value) => {
    const post = posts.find((p) => String(p._id) === String(postId));
    const pricePerHour = post?.priceValue || 0;
    setRowInputs((prev) => {
      const cur = prev[postId] || defaultRowInput();
      const next = { ...cur, [field]: value };

      if (field === "customer") {
        // Số đứng cuối ô khách thuê = số giờ. Chỉ tự điền khi con số này ĐỔI,
        // để admin vẫn sửa tay ô Số giờ/Hết hạn/Giá mà không bị ghi đè.
        // Bỏ acc-id ở đầu (nếu trùng title), rồi lấy SỐ hợp lệ cuối cùng làm số giờ.
        // Lọc giá trị < 1000 để loại acc-id (vd 5206) và SĐT; số có thể nằm giữa chuỗi ("3 km").
        let rest = value.trimStart();
        const title = (post?.title || "").trim();
        if (title && rest.startsWith(title)) rest = rest.slice(title.length);
        const nums = (rest.match(/\d+(?:[.,]\d+)?/g) || [])
          .map((s) => s.replace(",", "."))
          .filter((s) => { const n = parseFloat(s); return n > 0 && n < 1000; });
        const token = nums.length ? nums[nums.length - 1] : null;
        if (!value.trim()) {
          // Xoá trắng ô khách → reset luôn giờ + hết hạn + giá
          next.hours = "";
          next.endAt = "";
          next.priceAmount = "";
        } else if (token && token !== cur._custNumToken) {
          const h = parseFloat(token);
          if (h > 0) {
            next.hours = String(h);
            const s = new Date(next.startAt);
            if (!isNaN(s.getTime())) {
              next.endAt = toLocalInput(new Date(s.getTime() + h * 3600000));
              if (pricePerHour > 0) next.priceAmount = String(Math.round(h * pricePerHour));
            }
          }
        }
        next._custNumToken = token;
      } else if (field === "startAt" || field === "hours") {
        const s = new Date(next.startAt);
        const h = parseFloat(next.hours);
        if (!isNaN(s.getTime()) && h > 0) {
          next.endAt = toLocalInput(new Date(s.getTime() + h * 3600000));
          if (pricePerHour > 0) next.priceAmount = String(Math.round(h * pricePerHour));
        } else if (field === "hours" && !next.hours.trim()) {
          next.endAt = "";
          next.priceAmount = "";
        }
      } else if (field === "endAt") {
        const s = new Date(next.startAt);
        const e = new Date(next.endAt);
        if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && e > s) {
          const diffH = Math.round(((e - s) / 3600000) * 10) / 10;
          next.hours = String(diffH);
          if (pricePerHour > 0) next.priceAmount = String(Math.round(diffH * pricePerHour));
        }
      }

      return { ...prev, [postId]: next };
    });
  };

  const toggleExtend = (postId) => {
    setExtendHours((prev) => {
      const next = { ...prev };
      if (next[postId] !== undefined) delete next[postId];
      else next[postId] = "";
      return next;
    });
  };

  const handleExtendSave = async (post, bkg) => {
    const h = parseFloat(extendHours[post._id]);
    if (!h || h <= 0) return toast.error("Nhập số giờ gia hạn");
    const newEndAt = new Date(new Date(bkg.endAt).getTime() + h * 3600000);
    const addPrice = post.priceValue > 0 ? Math.round(h * post.priceValue) : 0;
    const newPriceAmount = (bkg.priceAmount || 0) + addPrice;
    try {
      await bookingsApi.update(bkg._id, {
        endAt: newEndAt.toISOString(),
        priceAmount: newPriceAmount,
      });
      toast.success(`Đã gia hạn thêm ${h}h cho ${post.title}`);
      setExtendHours((prev) => { const n = { ...prev }; delete n[post._id]; return n; });
      load(true);
      loadSummary();
    } catch {
      toast.error("Gia hạn thất bại");
    }
  };

  const startEditRow = (id, bkg) => {
    if (!bkg) return;
    const h = calcHours(bkg.startAt, bkg.endAt);
    setEditRow((prev) => ({
      ...prev,
      [id]: {
        customer: bkg.customerName || "",
        hours: h != null ? String(h) : "",
        startAt: toLocalInput(bkg.startAt),
        endAt: toLocalInput(bkg.endAt),
        priceAmount: bkg.priceAmount != null ? String(bkg.priceAmount) : "",
      },
    }));
  };
  const cancelEditRow = (id) =>
    setEditRow((prev) => { const n = { ...prev }; delete n[id]; return n; });

  // Liên kết 3 chiều khi sửa: giờ ↔ bắt đầu ↔ hết hạn, tự tính giá.
  // pricePerHourOverride: dùng cho bảng phiếu (không có "post" trong state posts).
  const setEditField = (id, field, value, pricePerHourOverride) => {
    const post = posts.find((p) => String(p._id) === String(id));
    const pricePerHour = pricePerHourOverride != null ? pricePerHourOverride : (post?.priceValue || 0);
    setEditRow((prev) => {
      const cur = prev[id] || {};
      const next = { ...cur, [field]: value };
      if (field === "startAt" || field === "hours") {
        const s = new Date(next.startAt);
        const h = parseFloat(next.hours);
        if (!isNaN(s.getTime()) && h > 0) {
          next.endAt = toLocalInput(new Date(s.getTime() + h * 3600000));
          if (pricePerHour > 0) next.priceAmount = String(Math.round(h * pricePerHour));
        }
      } else if (field === "endAt") {
        const s = new Date(next.startAt);
        const e = new Date(next.endAt);
        if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && e > s) {
          const diffH = Math.round(((e - s) / 3600000) * 10) / 10;
          next.hours = String(diffH);
          if (pricePerHour > 0) next.priceAmount = String(Math.round(diffH * pricePerHour));
        }
      }
      return { ...prev, [id]: next };
    });
  };

  const handleSaveRow = async (id, bkg) => {
    const e = editRow[id];
    if (!e || !bkg) return;
    const customer = (e.customer || "").trim();
    if (!customer) return toast.error("Nhập tên khách thuê");
    const startAt = new Date(e.startAt);
    const endAt = new Date(e.endAt);
    if (isNaN(startAt.getTime()) || isNaN(endAt.getTime()))
      return toast.error("Thời gian không hợp lệ");
    if (endAt <= startAt) return toast.error("Hết hạn phải sau bắt đầu");
    try {
      await bookingsApi.update(bkg._id, {
        customerName: customer,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        priceAmount: Number(e.priceAmount) || 0,
      });
      toast.success("Đã cập nhật phiếu thuê");
      cancelEditRow(id);
      load(true);
      loadSummary();
    } catch {
      toast.error("Cập nhật thất bại");
    }
  };

  const handleRentPost = async (post) => {
    const inp = rowInputs[post._id] || {};
    // Tôn trọng nguyên văn người dùng nhập, chỉ bỏ khoảng trắng thừa
    const customer = (inp.customer || "").trim();
    if (!customer) return toast.error("Nhập tên khách thuê");
    if (!inp.startAt) return toast.error("Chọn thời gian bắt đầu");
    if (!inp.endAt) return toast.error("Chọn thời gian hết hạn");
    const startAt = new Date(inp.startAt);
    const endAt = new Date(inp.endAt);
    if (isNaN(startAt.getTime()) || isNaN(endAt.getTime()))
      return toast.error("Thời gian không hợp lệ");
    if (endAt <= startAt) return toast.error("Hết hạn phải sau bắt đầu");

    setSubmitting((prev) => ({ ...prev, [post._id]: true }));
    try {
      const saved = await bookingsApi.create({
        postId: post._id,
        customerName: customer,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        price: post.price || "",
        priceAmount: Number(inp.priceAmount) || 0,
        username: user?.username || "",
      });
      toast.success(`Đã cho thuê ${post.title}`);
      // Cập nhật lạc quan TẠI CHỖ bằng phiếu server vừa trả về — KHÔNG reload cả
      // bảng. Reload nặng (getAllPosts + getAll) hay timeout vì cold-start, gây
      // spinner kéo dài + toast đỏ "Không tải được dữ liệu" ngay sau khi đã cho
      // thuê thành công. Socket + auto-refresh 60s vẫn lo phần đồng bộ chéo.
      setPosts((prev) => prev.map((p) =>
        String(p._id) === String(post._id)
          ? { ...p, rentalStatus: "rented", rentedUntil: endAt.toISOString() }
          : p));
      if (saved && saved._id) {
        setActiveBkgMap((prev) => ({ ...prev, [String(post._id)]: saved }));
      }
      setRowInputs((prev) => { const n = { ...prev }; delete n[post._id]; return n; });
      loadSummary(); // chỉ làm tươi thống kê (1 request nhẹ)
    } catch {
      toast.error("Tạo phiếu thuê thất bại");
    } finally {
      setSubmitting((prev) => ({ ...prev, [post._id]: false }));
    }
  };

  // Lọc posts theo tab game + search; tab "Trống" chỉ hiện acc chưa có ai thuê
  const filteredPosts = useMemo(() => {
    let arr = posts.filter((p) => gameOf(p.thumb) === game);
    if (isFreeTab) arr = arr.filter((p) => p.rentalStatus !== "rented");
    else if (isActiveTab) arr = arr.filter((p) => p.rentalStatus === "rented");
    if (!search.trim()) return arr;
    const kw = search.toLowerCase();
    return arr.filter((p) => (p.title || "").toLowerCase().includes(kw));
  }, [posts, search, game, isFreeTab, isActiveTab]);

  // Lọc bookings theo tab game + search (dùng cho tất cả các tab booking)
  const filteredBookings = useMemo(() => {
    let arr = bookings.filter((b) => gameOf(postThumbMap[String(b.postId)]) === game);
    if (!search.trim()) return arr;
    const kw = search.toLowerCase();
    return arr.filter(
      (b) =>
        (b.postTitle || "").toLowerCase().includes(kw) ||
        (b.customerName || "").toLowerCase().includes(kw)
    );
  }, [bookings, search, game, postThumbMap]);

  // Giá trị/giờ ĐẠI DIỆN cho mỗi gói (chuỗi giá). Phiếu cũ chưa denormalize
  // priceValue sẽ "mượn" giá trị từ phiếu mới CÙNG GÓI, nếu không sẽ bị tách
  // thành 2 cụm (vd "3h/75.000": phiếu cũ priceValue=0 → parse 75.000 vs phiếu
  // mới priceValue=25.000 — khác thang đo nên cùng nhãn vẫn ra 2 vách ngăn).
  const groupPvMap = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      const key = b.price || "";
      const pv = b.priceValue || 0;
      if (pv && pv > (map[key] || 0)) map[key] = pv;
    }
    return map;
  }, [bookings]);
  const groupPv = useCallback(
    (b) => groupPvMap[b.price || ""] || bookingPriceValue(b),
    [groupPvMap]
  );

  // Sắp phiếu để phân cụm:
  // - Tab "Đã trả": theo NGÀY KINH DOANH, mới nhất lên trước (trong ngày cũng mới nhất trước).
  // - Các tab khác: theo mức giá giảm dần như tab "Tất cả acc".
  // sort ổn định → giữ thứ tự mới nhất (createdAt) từ API trong cùng cụm.
  const sortedBookings = useMemo(() => {
    const arr = [...filteredBookings];
    if (isCompletedTab) {
      arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else {
      arr.sort((a, b) =>
        groupPv(b) - groupPv(a) ||
        parsePriceValue(b.price) - parsePriceValue(a.price) ||
        (a.price || "").localeCompare(b.price || ""));
    }
    return arr;
  }, [filteredBookings, groupPv, isCompletedTab]);

  // Tổng theo ngày kinh doanh (chỉ tab "Đã trả") để hiển thị ở vách ngăn ngày.
  const dayTotals = useMemo(() => {
    if (!isCompletedTab) return {};
    const m = {};
    for (const b of filteredBookings) {
      const k = businessDayOf(b.createdAt);
      if (!m[k]) m[k] = { count: 0, total: 0 };
      m[k].count += 1;
      m[k].total += b.priceAmount || 0;
    }
    return m;
  }, [filteredBookings, isCompletedTab]);

  // Màu vách ngăn theo gói (chuỗi giá) — mỗi gói một màu, kể cả khi cùng giá/giờ
  const postTierMap = useMemo(
    () => buildPriceTierMap(posts, (p) => p.priceValue, (p) => p.price),
    [posts]
  );
  const bookingTierMap = useMemo(
    () => buildPriceTierMap(filteredBookings, (b) => groupPv(b), (b) => b.price),
    [filteredBookings, groupPv]
  );

  // Số lượng hiển thị trên tab game (không tính từ khoá tìm kiếm)
  const gameCounts = useMemo(() => {
    const c = {};
    const bump = (g) => { c[g] = (c[g] || 0) + 1; };
    if (isPostsTab) {
      let arr = posts;
      if (isFreeTab) arr = arr.filter((p) => p.rentalStatus !== "rented");
      else if (isActiveTab) arr = arr.filter((p) => p.rentalStatus === "rented");
      arr.forEach((p) => bump(gameOf(p.thumb)));
    } else {
      bookings.forEach((b) => bump(gameOf(postThumbMap[String(b.postId)])));
    }
    return c;
  }, [isPostsTab, isFreeTab, isActiveTab, posts, bookings, postThumbMap]);

  // Bulk + single (booking tabs) — chỉ trên các phiếu đang hiển thị
  const allIds = useMemo(() => filteredBookings.map((b) => b._id), [filteredBookings]);
  const allChecked = allIds.length > 0 && selected.length === allIds.length;
  const toggleOne = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setSelected(allChecked ? [] : allIds);

  const runSingle = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(true); loadSummary(); }
    catch { toast.error("Thao tác thất bại"); }
  };
  // Hiện modal xác nhận; tự đóng modal trước khi chạy hành động
  const askConfirm = (cfg) => setConfirm(cfg);
  const closeConfirm = () => setConfirm(null);
  const runConfirm = () => {
    if (!confirm) return;
    const fn = confirm.onConfirm;
    setConfirm(null);
    fn();
  };

  // Copy nhanh id|user|pass (+ uy tín/rank nếu có) để dán gửi khách. Tab "Tất cả
  // acc" đã có post đầy đủ trong state (trừ acc login) → chỉ cần fetch creds;
  // các tab phiếu khác không có post trong cache → fetch luôn cả post.
  //
  // navigator.clipboard.write() phải được gọi ĐỒNG BỘ ngay trong click handler
  // để giữ "user activation" — Safari iOS / webview trong app (Zalo, Messenger…)
  // sẽ chặn clipboard nếu ghi sau một await (gọi API). Do dữ liệu cần fetch
  // trước, ta truyền vào ClipboardItem một Promise<string> — write() vẫn chạy
  // ngay trong gesture, còn nội dung text được resolve sau khi API trả về.
  const handleCopyAcc = (postId, fallbackTitle) => {
    const cached = posts.find((p) => String(p._id) === String(postId));
    let title = fallbackTitle;
    const textPromise = (async () => {
      const [postData, creds] = await Promise.all([
        cached ? Promise.resolve(cached) : postsApi.getPostById(postId),
        postsApi.getCredentials(postId),
      ]);
      if (!creds.accUsername && !creds.accPassword) {
        throw new Error("NO_CRED");
      }
      title = postData?.title || fallbackTitle;
      return buildAccCopyText(postData, creds);
    })();

    const notify = () => {
      textPromise
        .then(() => toast.success(`Đã copy thông tin ${title}`))
        .catch((err) => {
          toast.error(
            err?.message === "NO_CRED"
              ? "Acc chưa có tài khoản đăng nhập — bấm icon sửa để thêm"
              : "Copy thất bại"
          );
        });
    };

    if (window.ClipboardItem) {
      navigator.clipboard
        .write([new window.ClipboardItem({ "text/plain": textPromise })])
        .catch(() => {});
      notify();
    } else {
      // Trình duyệt không hỗ trợ ClipboardItem dữ liệu-Promise → đành ghi sau
      // await như cũ (đủ dùng cho desktop, các webview mobile hiếm gặp case này).
      textPromise
        .then((text) => navigator.clipboard.writeText(text))
        .then(() => toast.success(`Đã copy thông tin ${title}`))
        .catch((err) => {
          toast.error(
            err?.message === "NO_CRED"
              ? "Acc chưa có tài khoản đăng nhập — bấm icon sửa để thêm"
              : "Copy thất bại"
          );
        });
    }
  };

  const openAccEdit = (postId) => setAccEditId(postId);
  const closeAccEdit = () => setAccEditId(null);
  const handleAccSaved = () => { closeAccEdit(); load(true); };

  const handleComplete = (id) => askConfirm({
    title: "Kết thúc thuê",
    message: "Xác nhận kết thúc phiếu thuê này? Acc sẽ chuyển về trạng thái trống.",
    confirmLabel: "Kết thúc thuê",
    variant: "success",
    onConfirm: () => runSingle(() => bookingsApi.complete(id), "Đã kết thúc thuê"),
  });
  const handleCancel = (id) => askConfirm({
    title: "Hủy phiếu thuê",
    message: "Xác nhận hủy phiếu thuê này? Hành động này sẽ kết thúc phiếu mà không tính là đã trả.",
    confirmLabel: "Hủy thuê",
    variant: "warn",
    onConfirm: () => runSingle(() => bookingsApi.cancel(id), "Đã hủy phiếu"),
  });
  const handleRestore = (id) => runSingle(() => bookingsApi.restore(id), "Đã khôi phục phiếu");
  const handleDelete = (id) => askConfirm({
    title: "Chuyển vào thùng rác",
    message: "Chuyển phiếu này vào thùng rác?",
    confirmLabel: "Xóa",
    variant: "danger",
    onConfirm: () => runSingle(() => bookingsApi.remove(id), "Đã chuyển vào thùng rác"),
  });
  const handlePermanent = (id) => askConfirm({
    title: "Xóa vĩnh viễn",
    message: "Xóa vĩnh viễn phiếu này? Không thể khôi phục.",
    confirmLabel: "Xóa vĩnh viễn",
    variant: "danger",
    onConfirm: () => runSingle(() => bookingsApi.removePermanent(id), "Đã xóa vĩnh viễn"),
  });
  const doBulk = async (action, okMsg) => {
    if (selected.length === 0) return;
    try {
      const res = await bookingsApi.bulk(selected, action);
      toast.success(`${okMsg} (${res?.count ?? selected.length} phiếu)`);
      load();
      loadSummary();
    } catch {
      toast.error("Thao tác hàng loạt thất bại");
    }
  };

  return (
    <div className="bookings-page">
      {/* Tiêu đề trang đã chuyển lên header (components/TopBar) — hàng này chỉ
          còn 3 thẻ thống kê và ô ghi chú trong ngày lấp phần trống bên phải. */}
      <div className="bookings-header">
        <div className="bookings-stats">
          <div className="stat-card">
            <span className="stat-num">{summary.active}</span>
            <span className="stat-label">Đang cho thuê</span>
          </div>
          <div className={`stat-card ${summary.overdue > 0 ? "danger" : ""}`}>
            <span className="stat-num">{summary.overdue}</span>
            <span className="stat-label">Quá hạn</span>
          </div>
          <div className={`stat-card revenue ${showUnlockForm && !revenueUnlocked ? "flipped" : ""}`}>
            <div className="revenue-flip-inner">
              <div className="revenue-face revenue-face-front">
                {revenueUnlocked ? (
                  <>
                    <span className="stat-num stat-revenue">{formatCurrency(revenueValue)}</span>
                    <span className="stat-label">
                      {isRevenueToday ? "Doanh thu hôm nay" : `Doanh thu ${formatDateStr(revenueDate)}`}
                      <button className="revenue-relock-btn" title="Ẩn doanh thu" onClick={handleLockRevenue}>
                        <i className="fas fa-eye-slash"></i>
                      </button>
                    </span>
                    <div className="revenue-date-nav">
                      <div className="rev-date-pill">
                        <button
                          className="rev-nav"
                          title="Ngày trước"
                          onClick={() => setRevenueDate((d) => shiftDateStr(d, -1))}
                        >
                          <i className="fas fa-chevron-left"></i>
                        </button>
                        <button className="rev-date-display" title="Chọn ngày" onClick={openDatePicker}>
                          <i className="far fa-calendar-alt"></i>
                          <span>{formatDateStr(revenueDate)}</span>
                        </button>
                        <button
                          className="rev-nav"
                          title="Ngày sau"
                          disabled={!canGoNext}
                          onClick={() => setRevenueDate((d) => shiftDateStr(d, 1))}
                        >
                          <i className="fas fa-chevron-right"></i>
                        </button>
                        <input
                          ref={dateInputRef}
                          type="date"
                          className="rev-date-hidden"
                          value={revenueDate}
                          max={maxDateStr}
                          onChange={(e) => e.target.value && setRevenueDate(e.target.value)}
                          tabIndex={-1}
                          aria-hidden="true"
                        />
                      </div>
                      {!isRevenueToday && (
                        <button className="rev-today-btn" onClick={() => setRevenueDate(todayStr)}>
                          Hôm nay
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="brand-card"
                    title="Xem doanh thu"
                    onClick={() => setShowUnlockForm(true)}
                  >
                    <span className="brand-card-badge">CF</span>
                    <span className="brand-card-name">HoangLeCF</span>
                    <span className="brand-card-tagline">Cho thuê tài khoản</span>
                  </button>
                )}
              </div>
              <div className="revenue-face revenue-face-back">
                <button
                  type="button"
                  className="revenue-back-btn"
                  title="Quay lại"
                  onClick={() => { setShowUnlockForm(false); setRevenueErr(""); setRevenueInput(""); }}
                >
                  <i className="fas fa-arrow-left"></i>
                </button>
                <div className="revenue-lock">
                  <input
                    type="password"
                    className="revenue-lock-input"
                    placeholder="Mật khẩu"
                    autoFocus={showUnlockForm}
                    value={revenueInput}
                    disabled={revenueChecking}
                    onChange={(e) => { setRevenueInput(e.target.value); setRevenueErr(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleUnlockRevenue()}
                  />
                  <button
                    className="revenue-lock-btn"
                    title="Xem doanh thu"
                    disabled={revenueChecking || !revenueInput.trim()}
                    onClick={handleUnlockRevenue}
                  >
                    {revenueChecking ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-lock"></i>}
                  </button>
                  {revenueErr && <span className="revenue-lock-err">{revenueErr}</span>}
                </div>
                <div className="revenue-date-nav">
                  <div className="rev-date-pill">
                    <button
                      className="rev-nav"
                      title="Ngày trước"
                      onClick={() => setRevenueDate((d) => shiftDateStr(d, -1))}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button className="rev-date-display" title="Chọn ngày" onClick={openDatePicker}>
                      <i className="far fa-calendar-alt"></i>
                      <span>{formatDateStr(revenueDate)}</span>
                    </button>
                    <button
                      className="rev-nav"
                      title="Ngày sau"
                      disabled={!canGoNext}
                      onClick={() => setRevenueDate((d) => shiftDateStr(d, 1))}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                    <input
                      ref={dateInputRef}
                      type="date"
                      className="rev-date-hidden"
                      value={revenueDate}
                      max={maxDateStr}
                      onChange={(e) => e.target.value && setRevenueDate(e.target.value)}
                      tabIndex={-1}
                      aria-hidden="true"
                    />
                  </div>
                  {!isRevenueToday && (
                    <button className="rev-today-btn" onClick={() => setRevenueDate(todayStr)}>
                      Hôm nay
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <DailyNote date={todayStr} />
      </div>

      <div className="game-tabs" role="tablist" aria-label="Loại tài khoản">
        {GAME_TABS.map((g) => (
          <button
            key={g.key}
            role="tab"
            aria-selected={game === g.key}
            className={`game-tab ${game === g.key ? "active" : ""}`}
            onClick={() => { setGame(g.key); setSelected([]); }}
          >
            <i className={g.icon}></i>
            <span>{g.label}</span>
            {gameCounts[g.key] > 0 && <span className="game-tab-count">{gameCounts[g.key]}</span>}
          </button>
        ))}
      </div>

      <div className="bookings-topbar">
        <div className="bookings-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={filter === f.key ? "active" : ""}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {f.key === "deleted" && summary.deleted > 0 ? ` (${summary.deleted})` : ""}
            </button>
          ))}
        </div>
        <div className="posts-search-wrap">
          <i className="fas fa-search posts-search-icon"></i>
          <input
            className="posts-search"
            placeholder="Tìm acc, khách thuê..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="posts-search-clear" onClick={() => setSearch("")}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      </div>

      {!isPostsTab && selected.length > 0 && (
        <div className="bulk-bar">
          <span className="bulk-count"><i className="fas fa-check-square"></i> Đã chọn {selected.length}</span>
          <div className="bulk-actions">
            {isDeletedTab ? (
              <>
                <button className="btn btn-success" onClick={() => doBulk("restore", "Đã khôi phục")}>
                  <i className="fas fa-trash-restore"></i> Khôi phục
                </button>
                <button className="btn btn-danger" onClick={() => askConfirm({
                  title: "Xóa vĩnh viễn",
                  message: `Xóa vĩnh viễn ${selected.length} phiếu đã chọn? Không thể khôi phục.`,
                  confirmLabel: "Xóa vĩnh viễn",
                  variant: "danger",
                  onConfirm: () => doBulk("permanent", "Đã xóa vĩnh viễn"),
                })}>
                  <i className="fas fa-trash"></i> Xóa vĩnh viễn
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-success" onClick={() => askConfirm({
                  title: "Kết thúc thuê",
                  message: `Xác nhận kết thúc ${selected.length} phiếu thuê đã chọn?`,
                  confirmLabel: "Kết thúc thuê",
                  variant: "success",
                  onConfirm: () => doBulk("complete", "Đã kết thúc thuê"),
                })}>
                  <i className="fas fa-check"></i> Kết thúc thuê
                </button>
                <button className="btn btn-warn" onClick={() => askConfirm({
                  title: "Hủy thuê",
                  message: `Xác nhận hủy ${selected.length} phiếu thuê đã chọn?`,
                  confirmLabel: "Hủy thuê",
                  variant: "warn",
                  onConfirm: () => doBulk("cancel", "Đã hủy"),
                })}>
                  <i className="fas fa-ban"></i> Hủy thuê
                </button>
                <button className="btn btn-danger" onClick={() => askConfirm({
                  title: "Chuyển vào thùng rác",
                  message: `Chuyển ${selected.length} phiếu đã chọn vào thùng rác?`,
                  confirmLabel: "Xóa",
                  variant: "danger",
                  onConfirm: () => doBulk("delete", "Đã chuyển vào thùng rác"),
                })}>
                  <i className="fas fa-trash"></i> Xóa
                </button>
              </>
            )}
            <button className="btn btn-secondary" onClick={() => setSelected([])}>Bỏ chọn</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bookings-empty">Đang tải...</div>
      ) : isPostsTab ? (
        <>
          {filteredPosts.length === 0 ? (
            <div className="bookings-empty">Không có acc {gameLabel} nào.</div>
          ) : (
            <div className="bookings-table-wrap">
              <table className="bookings-table posts-table">
                <thead>
                  <tr>
                    <th className="col-acc">Acc</th>
                    <th className="col-customer">Khách thuê</th>
                    <th className="col-hours">Số giờ</th>
                    {SHOW_PRICE_COLUMN && <th className="col-price">Giá (₫)</th>}
                    <th className="col-dt">Bắt đầu</th>
                    <th className="col-dt">Hết hạn</th>
                    <th className="col-act">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map((post, idx) => {
                    const bkg = activeBkgMap[String(post._id)];
                    const isRented = post.rentalStatus === "rented";
                    const inp = rowInputs[post._id] || defaultRowInput();
                    const isSub = !!submitting[post._id];
                    const isEditing = isRented && editRow[post._id] !== undefined;
                    const ed = editRow[post._id] || {};
                    const isExtending = isRented && !isEditing && extendHours[post._id] !== undefined;
                    const extH = isExtending ? parseFloat(extendHours[post._id] || "0") : 0;
                    const addPrice = extH > 0 && post.priceValue > 0 ? Math.round(extH * post.priceValue) : 0;
                    const newEndAt = extH > 0 && bkg?.endAt ? new Date(new Date(bkg.endAt).getTime() + extH * 3600000) : null;
                    // Ranh giới phân cụm: đổi mức giá/giờ HOẶC đổi gói (chuỗi giá)
                    const prev = idx > 0 ? filteredPosts[idx - 1] : null;
                    const showDivider = !prev
                      || (prev.priceValue || 0) !== (post.priceValue || 0)
                      || (prev.price || "") !== (post.price || "");
                    // Màu vách ngăn theo gói (mỗi gói một màu, kể cả cùng giá/giờ)
                    const tierClass = postTierMap[post.price || ""] || priceTierClass(post.priceValue || 0);
                    return (
                      <Fragment key={post._id}>
                      {showDivider && (
                        <tr className={`price-group-divider ${tierClass}`}>
                          <td colSpan={6 + (SHOW_PRICE_COLUMN ? 1 : 0)}>
                            <span className="price-group-label">{post.price || "Chưa đặt giá"}</span>
                          </td>
                        </tr>
                      )}
                      <tr className={`${isRented ? "rented-row" : "available-row"}${isExtending ? " extending-row" : ""}`}>
                        <td className="col-acc">
                          <AccCell
                            postId={post._id}
                            title={post.title}
                            viewTo={`/post/${post._id}`}
                            onCopy={() => handleCopyAcc(post._id, post.title)}
                            onEditAcc={() => openAccEdit(post._id)}
                            showAccTools={isLienQuanTab}
                          />
                          {isRented && !isEditing && (
                            <button className="acc-edit-btn acc-edit-booking-btn" title="Sửa phiếu thuê" onClick={() => startEditRow(post._id, bkg)}>
                              <i className="fas fa-pencil-alt"></i>
                            </button>
                          )}
                          <span className={`status-badge ${isRented ? "active" : "available-badge"} acc-status-badge`}>
                            {isRented ? "Đang thuê" : "Trống"}
                          </span>
                        </td>
                        <td className="col-customer">
                          {isRented ? (
                            isEditing ? (
                              <input
                                className="inline-input"
                                autoFocus
                                placeholder="Tên khách thuê"
                                value={ed.customer}
                                onChange={(e) => setEditField(post._id, "customer", e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleSaveRow(post._id, bkg);
                                  if (e.key === "Escape") cancelEditRow(post._id);
                                }}
                              />
                            ) : (
                              <span className="cell-text">
                                {bkg?.customerName || "—"}
                                <SourceTag source={bkg?.source} manuallyEditedAt={bkg?.manuallyEditedAt} />
                              </span>
                            )
                          ) : (
                            <input
                              className="inline-input"
                              placeholder="Tên khách… số cuối = giờ (vd: fb Nam 3)"
                              value={inp.customer}
                              onChange={(e) => setRowField(post._id, "customer", e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && handleRentPost(post)}
                            />
                          )}
                        </td>
                        {/* Số giờ */}
                        <td className="col-hours">
                          {isRented ? (
                            isEditing ? (
                              <input
                                className="inline-input hours-input"
                                type="number"
                                min="0.5"
                                step="0.5"
                                placeholder="giờ"
                                value={ed.hours}
                                onChange={(e) => setEditField(post._id, "hours", e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleSaveRow(post._id, bkg);
                                  if (e.key === "Escape") cancelEditRow(post._id);
                                }}
                              />
                            ) : (
                              (() => {
                                const h = calcHours(bkg?.startAt, bkg?.endAt);
                                return <span className="cell-text">{h != null ? `${h}h` : "—"}</span>;
                              })()
                            )
                          ) : (
                            <input
                              className="inline-input hours-input"
                              type="number"
                              min="0.5"
                              step="0.5"
                              placeholder="giờ"
                              value={inp.hours}
                              onChange={(e) => setRowField(post._id, "hours", e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && handleRentPost(post)}
                            />
                          )}
                        </td>
                        {/* Giá */}
                        {SHOW_PRICE_COLUMN && (
                        <td className="col-price">
                          {isRented ? (
                            isEditing ? (
                              <div className="price-cell">
                                <input
                                  className="inline-input price-input"
                                  type="number"
                                  min="0"
                                  step="1000"
                                  placeholder="0"
                                  value={ed.priceAmount}
                                  onChange={(e) => setEditField(post._id, "priceAmount", e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSaveRow(post._id, bkg);
                                    if (e.key === "Escape") cancelEditRow(post._id);
                                  }}
                                />
                                {post.priceValue > 0 && (
                                  <span className="price-hint">{formatCurrency(post.priceValue)}/h</span>
                                )}
                              </div>
                            ) : (
                              <span className="cell-text">{bkg?.priceAmount != null ? formatCurrency(bkg.priceAmount) : (bkg?.price || "—")}</span>
                            )
                          ) : (
                            <div className="price-cell">
                              <input
                                className="inline-input price-input"
                                type="number"
                                min="0"
                                step="1000"
                                placeholder="0"
                                value={inp.priceAmount}
                                onChange={(e) => setRowField(post._id, "priceAmount", e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleRentPost(post)}
                              />
                              {post.priceValue > 0 && (
                                <span className="price-hint">{formatCurrency(post.priceValue)}/h</span>
                              )}
                            </div>
                          )}
                        </td>
                        )}
                        {/* Bắt đầu */}
                        <td className="col-dt">
                          {isRented ? (
                            isEditing ? (
                              <input
                                type="datetime-local"
                                className="inline-input dt-input"
                                value={ed.startAt}
                                onChange={(e) => setEditField(post._id, "startAt", e.target.value)}
                              />
                            ) : (
                              <span className="cell-text">{formatDateTime(bkg?.startAt)}</span>
                            )
                          ) : (
                            <input
                              type="datetime-local"
                              className="inline-input dt-input"
                              value={inp.startAt}
                              onChange={(e) => setRowField(post._id, "startAt", e.target.value)}
                            />
                          )}
                        </td>
                        {/* Hết hạn */}
                        <td className="col-dt">
                          {isRented ? (
                            isEditing ? (
                              <input
                                type="datetime-local"
                                className="inline-input dt-input calc-end-input"
                                value={ed.endAt}
                                onChange={(e) => setEditField(post._id, "endAt", e.target.value)}
                              />
                            ) : (
                            <div className="endat-cell">
                              <EndDateTime date={bkg?.endAt} />
                              {isExtending ? (
                                <>
                                  <div className="extend-input-group">
                                    <input
                                      className="extend-hours-input"
                                      type="number"
                                      min="0.5"
                                      step="0.5"
                                      placeholder="+giờ"
                                      autoFocus
                                      value={extendHours[post._id]}
                                      onChange={(e) => setExtendHours((prev) => ({ ...prev, [post._id]: e.target.value }))}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleExtendSave(post, bkg);
                                        if (e.key === "Escape") toggleExtend(post._id);
                                      }}
                                    />
                                    <button className="extend-accept-btn" title="Lưu gia hạn" onClick={() => handleExtendSave(post, bkg)}>
                                      <i className="fas fa-check"></i>
                                    </button>
                                    <button className="extend-cancel-btn" title="Huỷ" onClick={() => toggleExtend(post._id)}>
                                      <i className="fas fa-times"></i>
                                    </button>
                                  </div>
                                  {newEndAt && <span className="extend-new-end">{formatDateTime(newEndAt)}</span>}
                                  {addPrice > 0 && <span className="extend-delta">+{formatCurrency(addPrice)}</span>}
                                </>
                              ) : (
                                <>
                                  <button className="extend-inline-btn" title="Gia hạn thêm giờ" onClick={() => toggleExtend(post._id)}>
                                    <i className="fas fa-clock"></i> +giờ
                                  </button>
                                </>
                              )}
                            </div>
                            )
                          ) : (
                            <input
                              type="datetime-local"
                              className={`inline-input dt-input ${inp.endAt ? "calc-end-input" : ""}`}
                              value={inp.endAt}
                              onChange={(e) => setRowField(post._id, "endAt", e.target.value)}
                            />
                          )}
                        </td>
                        <td className="col-act">
                          <div className="row-actions">
                            {isRented ? (
                              isEditing ? (
                                <>
                                  <button
                                    className="mini-btn success"
                                    title="Lưu thay đổi"
                                    onClick={() => handleSaveRow(post._id, bkg)}
                                  >
                                    <i className="fas fa-check"></i>
                                  </button>
                                  <button
                                    className="mini-btn danger"
                                    title="Huỷ sửa"
                                    onClick={() => cancelEditRow(post._id)}
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </>
                              ) : (
                              <>
                                <button
                                  className="mini-btn success"
                                  title="Kết thúc thuê"
                                  onClick={() => bkg && handleComplete(bkg._id)}
                                >
                                  <i className="fas fa-check"></i>
                                </button>
                                <button
                                  className="mini-btn warn"
                                  title="Huỷ phiếu"
                                  onClick={() => bkg && handleCancel(bkg._id)}
                                >
                                  <i className="fas fa-ban"></i>
                                </button>
                              </>
                              )
                            ) : (
                              <button
                                className="mini-btn rent-btn"
                                title="Cho thuê"
                                disabled={isSub}
                                onClick={() => handleRentPost(post)}
                              >
                                {isSub
                                  ? <i className="fas fa-spinner fa-spin"></i>
                                  : <i className="fas fa-play"></i>}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        filteredBookings.length === 0 ? (
          <div className="bookings-empty">
            {search.trim() ? "Không tìm thấy kết quả." : isDeletedTab ? "Thùng rác trống." : `Không có phiếu thuê ${gameLabel} nào.`}
          </div>
        ) : (
          <div className="bookings-table-wrap">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th className="col-check">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Chọn tất cả" />
                  </th>
                  <th>Acc</th>
                  <th>Khách thuê</th>
                  <th>Bắt đầu</th>
                  <th>Hết hạn</th>
                  {(SHOW_PRICE_COLUMN || isCompletedTab) && <th>Giá (₫)</th>}
                  <th>Trạng thái</th>
                  <th>Hành động</th>
                </tr>
              </thead>
              <tbody>
                {sortedBookings.map((b, idx) => {
                  const prev = idx > 0 ? sortedBookings[idx - 1] : null;
                  // Tab "Đã trả": vách ngăn theo NGÀY. Tab khác: theo dải giá.
                  let showDivider, dividerClass, dividerLabel;
                  if (isCompletedTab) {
                    const day = businessDayOf(b.createdAt);
                    showDivider = !prev || businessDayOf(prev.createdAt) !== day;
                    dividerClass = "day-divider";
                    const dt = dayTotals[day];
                    dividerLabel = dt
                      ? `${formatDateStr(day)} · ${dt.count} phiếu · ${formatCurrency(dt.total)}`
                      : formatDateStr(day);
                  } else {
                    // Ranh giới phân cụm: đổi mức giá/giờ HOẶC đổi gói (chuỗi giá)
                    showDivider = !prev
                      || groupPv(prev) !== groupPv(b)
                      || (prev.price || "") !== (b.price || "");
                    dividerClass = bookingTierMap[b.price || ""] || priceTierClass(groupPv(b));
                    dividerLabel = b.price || "Chưa đặt giá";
                  }
                  const isEditingB = !isDeletedTab && editRow[b._id] !== undefined;
                  const edB = editRow[b._id] || {};
                  return (
                  <Fragment key={b._id}>
                  {showDivider && (
                    <tr className={`price-group-divider ${dividerClass}`}>
                      <td colSpan={7 + ((SHOW_PRICE_COLUMN || isCompletedTab) ? 1 : 0)}>
                        <span className="price-group-label">{dividerLabel}</span>
                      </td>
                    </tr>
                  )}
                  <tr className={`${b.isOverdue ? "overdue-row" : ""} ${selected.includes(b._id) ? "selected-row" : ""}`}>
                    <td className="col-check">
                      <input
                        type="checkbox"
                        checked={selected.includes(b._id)}
                        onChange={() => toggleOne(b._id)}
                      />
                    </td>
                    <td>
                      <AccCell
                        postId={b.postId}
                        title={b.postTitle || b.postId}
                        viewTo={`/post/${b.postId}`}
                        onCopy={() => handleCopyAcc(b.postId, b.postTitle)}
                        onEditAcc={() => openAccEdit(b.postId)}
                        showAccTools={isLienQuanTab}
                      />
                    </td>
                    <td>
                      {isEditingB ? (
                        <input
                          className="inline-input"
                          autoFocus
                          placeholder="Tên khách thuê"
                          value={edB.customer}
                          onChange={(e) => setEditField(b._id, "customer", e.target.value, bookingPriceValue(b))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveRow(b._id, b);
                            if (e.key === "Escape") cancelEditRow(b._id);
                          }}
                        />
                      ) : (
                        <>
                          {b.customerName}{b.customerPhone ? ` - ${b.customerPhone}` : ""}
                          <SourceTag source={b.source} manuallyEditedAt={b.manuallyEditedAt} />
                        </>
                      )}
                    </td>
                    <td>
                      {isEditingB ? (
                        <input
                          type="datetime-local"
                          className="inline-input dt-input"
                          value={edB.startAt}
                          onChange={(e) => setEditField(b._id, "startAt", e.target.value, bookingPriceValue(b))}
                        />
                      ) : formatDateTime(b.startAt)}
                    </td>
                    <td>
                      {isEditingB ? (
                        <input
                          type="datetime-local"
                          className="inline-input dt-input calc-end-input"
                          value={edB.endAt}
                          onChange={(e) => setEditField(b._id, "endAt", e.target.value, bookingPriceValue(b))}
                        />
                      ) : <EndDateTime date={b.endAt} />}
                    </td>
                    {(SHOW_PRICE_COLUMN || isCompletedTab) && (
                    <td>
                      {isEditingB ? (
                        <input
                          className="inline-input price-input"
                          type="number"
                          min="0"
                          step="1000"
                          placeholder="0"
                          value={edB.priceAmount}
                          onChange={(e) => setEditField(b._id, "priceAmount", e.target.value, bookingPriceValue(b))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveRow(b._id, b);
                            if (e.key === "Escape") cancelEditRow(b._id);
                          }}
                        />
                      ) : (
                        b.priceAmount != null
                          ? <span className="price-tag">{formatCurrency(b.priceAmount)}</span>
                          : <span className="muted-text">{b.price || "—"}</span>
                      )}
                    </td>
                    )}
                    <td>
                      {isDeletedTab ? (
                        <span className="status-badge cancelled">Đã xóa</span>
                      ) : (
                        <>
                          {b.status === "active" && b.isOverdue && <span className="status-badge overdue">Quá hạn</span>}
                          {b.status === "active" && !b.isOverdue && <span className="status-badge active">Đang thuê</span>}
                          {b.status === "completed" && <span className="status-badge completed">Đã trả</span>}
                          {b.status === "cancelled" && <span className="status-badge cancelled">Đã hủy</span>}
                        </>
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        {isDeletedTab ? (
                          <>
                            <button className="mini-btn success" title="Khôi phục" onClick={() => handleRestore(b._id)}>
                              <i className="fas fa-trash-restore"></i>
                            </button>
                            <button className="mini-btn danger" title="Xóa vĩnh viễn" onClick={() => handlePermanent(b._id)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </>
                        ) : isEditingB ? (
                          <>
                            <button className="mini-btn success" title="Lưu thay đổi" onClick={() => handleSaveRow(b._id, b)}>
                              <i className="fas fa-check"></i>
                            </button>
                            <button className="mini-btn danger" title="Huỷ sửa" onClick={() => cancelEditRow(b._id)}>
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="mini-btn edit" title="Sửa phiếu thuê" onClick={() => startEditRow(b._id, b)}>
                              <i className="fas fa-pencil-alt"></i>
                            </button>
                            {b.status === "active" && (
                              <>
                                <button className="mini-btn success" title="Kết thúc thuê" onClick={() => handleComplete(b._id)}>
                                  <i className="fas fa-check"></i>
                                </button>
                                <button className="mini-btn warn" title="Hủy phiếu" onClick={() => handleCancel(b._id)}>
                                  <i className="fas fa-ban"></i>
                                </button>
                              </>
                            )}
                            <button className="mini-btn danger" title="Xóa" onClick={() => handleDelete(b._id)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {confirm && (
        <div className="confirm-backdrop" onClick={closeConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className={`confirm-icon ${confirm.variant || ""}`}>
              <i className={
                confirm.variant === "success" ? "fas fa-check"
                : confirm.variant === "danger" ? "fas fa-trash"
                : "fas fa-exclamation-triangle"
              }></i>
            </div>
            <h3 className="confirm-title">{confirm.title}</h3>
            <p className="confirm-message">{confirm.message}</p>
            <div className="confirm-actions">
              <button className="btn btn-secondary" onClick={closeConfirm}>Thoát</button>
              <button className={`btn btn-${confirm.variant || "primary"}`} onClick={runConfirm} autoFocus>
                {confirm.confirmLabel || "Xác nhận"}
              </button>
            </div>
          </div>
        </div>
      )}

      {accEditId && (
        <AccEditModal
          postId={accEditId}
          username={user?.username}
          onClose={closeAccEdit}
          onSaved={handleAccSaved}
        />
      )}
    </div>
  );
}
